import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  AlertController,
  IonButton,
  IonIcon,
  LoadingController,
  ToastController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { cloudOfflineOutline } from 'ionicons/icons';

import { DeleteZoneMessage } from '../../../../../common/enums/delete-zone-message.enum';
import { LoadingMessage } from '../../../../../common/enums/loading-message.enum';
import { SyncMessage } from '../../../../../common/enums/sync-message.enum';
import { ToastLife } from '../../../../../common/enums/toast-life.enum';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import { SyncService } from '../../sync/sync.service';

/**
 * "Delete all Cloud data" — the whole-backup wipe, last on the Cloud backup
 * sheet.
 *
 * Its own component rather than the tail of the cloud items list, because it is
 * not part of that list: it destroys the backup wholesale, including items the
 * list is still loading or failed to load. Separating it is also what lets the
 * Failed backups section sit between the list and this — the destructive action
 * belongs at the very bottom, after everything it would destroy.
 *
 * Double-confirmed, and it says both halves of what happens: the Cloud copy is
 * gone for good, the device keeps everything.
 */
@Component({
  selector: 'app-cloud-wipe-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon],
  template: `
    <div class="wipe-card surface" data-testid="ci-wipe-all">
      <ion-icon name="cloud-offline-outline" color="danger" aria-hidden="true"></ion-icon>
      <div>
        <div class="item-title">Delete all Cloud data</div>
        <p class="muted">
          Erases your entire Cloud backup — your whole library and media in one go.
          Data on this device is kept.
        </p>
      </div>
      <ion-button expand="block" color="danger" (click)="purge()">
        Delete all Cloud data
      </ion-button>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .wipe-card { margin-top: 24px; padding: 16px; border-radius: 14px; }
    .wipe-card ion-icon { font-size: 28px; }
    .item-title { font-weight: 700; letter-spacing: -0.02em; margin-top: 8px; }
    .muted { color: var(--ion-color-medium); font-size: 0.85rem; margin-bottom: 12px; }
  `],
})
export class CloudWipeCardComponent {
  private readonly sync = inject(SyncService);
  private readonly alertCtrl = inject(AlertController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly toastCtrl = inject(ToastController);
  private readonly logger = inject(LogFactory).createLogger(CloudWipeCardComponent);

  constructor() {
    addIcons({ cloudOfflineOutline });
  }

  protected async purge(): Promise<void> {
    const ok = await this.doubleConfirm(
      'Delete all Cloud data?',
      'All your backed-up content and media will be permanently deleted from the Cloud — ' +
        "your whole library. Data on this device is kept. This can't be undone.",
      'Your Cloud backup will be gone forever. Data on this device stays.',
    );
    if (!ok) return;
    const loading = await this.loadingCtrl.create({
      message: LoadingMessage.Deleting,
      spinner: 'crescent',
      backdropDismiss: false,
    });
    await loading.present();
    try {
      const result = await this.sync.purgeAllCloudData();
      if (!result.purged) {
        // Nothing defers a purge today, but never report a delete that did not
        // happen — the server answers `purged: false` rather than throwing.
        await this.info(DeleteZoneMessage.CloudDeleteDeferred);
        return;
      }
      await this.sync.refreshStorageUsageQuietly();
      await this.toast(DeleteZoneMessage.CloudDataDeleted);
    } catch (err) {
      this.logger.error('Cloud data delete failed', err);
      await this.toast(DeleteZoneMessage.CloudDeleteFailed, 'danger');
    } finally {
      await loading.dismiss();
    }
  }

  private async doubleConfirm(
    header: string,
    message: string,
    secondMessage: string,
  ): Promise<boolean> {
    const first = await this.confirm(header, message, DeleteZoneMessage.ContinueButton);
    if (!first) return false;
    return this.confirm(
      DeleteZoneMessage.SecondConfirmHeader,
      secondMessage,
      DeleteZoneMessage.DeleteForeverButton,
    );
  }

  private async confirm(header: string, message: string, confirmText: string): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: [
        { text: DeleteZoneMessage.CancelButton, role: 'cancel' },
        { text: confirmText, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private async info(message: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: SyncMessage.CloudItemsTitle,
      message,
      buttons: [DeleteZoneMessage.CloseButton],
    });
    await alert.present();
  }

  private async toast(message: string, color = 'medium'): Promise<void> {
    const t = await this.toastCtrl.create({
      message,
      color,
      duration: ToastLife.SHORT,
      position: 'bottom',
    });
    await t.present();
  }
}
